import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowUpRight, ArrowRight, CheckCircle2,
  GitBranch, Database, ShieldCheck, FileBarChart2, Webhook, KeyRound, ListTree, Activity,
} from "lucide-react";
import CodeRain from "@/components/marketing/CodeRain";
import { useCodeRain } from "@/lib/siteEffects";

/**
 * /enterprise — "Infrastructure" marketing page.
 *
 * Cal.com / Linear-grade simplicity: single-column text-led hero, three
 * supporting stats, feature grid, SRE metrics, CTA. No diagrams, no
 * schematic illustrations — the page now reads like enterprise documentation,
 * not a product demo.
 */
const FEATURES = [
  { Icon: GitBranch,     t: "Ping Trees",          d: "Multi-buyer waterfalls with per-buyer caps, schedules, geographic logic and fallback branches." },
  { Icon: Database,      t: "Lead Routing Engine", d: "Direct-post and ping/post in parallel, with retry, dedupe and per-source A/B." },
  { Icon: Webhook,       t: "Webhook Fan-Out",     d: "Deliver each lead to unlimited destinations — CRMs, dialers, BI warehouses." },
  { Icon: KeyRound,      t: "API Delivery",        d: "REST + signed webhooks. Schema-validated payloads, idempotent retries." },
  { Icon: ShieldCheck,   t: "Compliance Center",   d: "Consent log, TrustedForm, suppression, DSAR, audit trail — exportable." },
  { Icon: FileBarChart2, t: "Reporting",           d: "Cohort, funnel, per-source P&L. Scheduled deliveries to Slack and email." },
  { Icon: ListTree,      t: "Buyer Management",    d: "Roster, caps, pricing schedules, dispute resolution flows." },
  { Icon: Activity,      t: "Realtime Health",     d: "Routing latency, acceptance, error budgets — live SRE-grade dashboards." },
];

const HERO_STATS = [
  { v: "99.99%", l: "Uptime SLA" },
  { v: "98ms",   l: "Median routing latency" },
  { v: "EU-1",   l: "Data residency" },
];

export default function Enterprise() {
  const rain = useCodeRain("enterprise");
  return (
    <div className="bg-white">
      {/* ── HERO — single-column, text-first ─────────────────────────────── */}
      <section className="relative overflow-hidden border-b border-slate-200/80" data-testid="enterprise-hero">
        <div className="absolute inset-0 dot-grid dot-grid-fade opacity-70" aria-hidden />
        <div className="absolute top-24 left-1/2 -translate-x-1/2 w-[680px] h-[680px] rounded-full bg-primary/5 blur-3xl" aria-hidden />

        <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 pt-20 lg:pt-28 pb-16 lg:pb-20 text-center">
          <Badge
            variant="outline"
            className="rounded-full border-slate-300 bg-white/70 backdrop-blur text-slate-700 px-3 py-1"
            data-testid="enterprise-hero-badge"
          >
            <span className="size-1.5 rounded-full bg-emerald-500 mr-2 animate-pulse" />
            Enterprise Infrastructure
          </Badge>

          <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-[-0.04em] text-slate-900 leading-[1.04]">
            The operational backbone of
            {" "}<span className="gradient-text">financial acquisition.</span>
          </h1>

          <p className="mt-6 text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
            Routing, billing, compliance, reporting, affiliate and buyer management —
            engineered as one system, accountable to one team. The operator view your
            SREs will live in.
          </p>

          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/book-demo">
              <Button data-testid="enterprise-cta-primary" className="rounded-full px-5 h-11 text-[15px]">
                Talk to engineering <ArrowUpRight className="size-4" />
              </Button>
            </Link>
            <Link to="/activity">
              <Button data-testid="enterprise-cta-secondary" variant="outline" className="rounded-full px-5 h-11 text-[15px] border-slate-300">
                See it live <ArrowRight className="size-4" />
              </Button>
            </Link>
          </div>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-xs text-slate-500">
            <span className="inline-flex items-center gap-2"><CheckCircle2 className="size-3.5 text-primary" /> mTLS 1.3 ingress · EU-residency</span>
            <span className="inline-flex items-center gap-2"><CheckCircle2 className="size-3.5 text-primary" /> 4 PoPs · &lt;10ms regional</span>
            <span className="inline-flex items-center gap-2"><CheckCircle2 className="size-3.5 text-primary" /> Signed webhook fan-out</span>
          </div>
        </div>

        {/* Stat strip — narrow, mono, separated from hero by hairline */}
        <div className="relative border-t border-slate-200/80 bg-white/40">
          <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-6 grid grid-cols-3 divide-x divide-slate-200">
            {HERO_STATS.map((s, i) => (
              <div key={i} className="text-center" data-testid={`hero-stat-${i}`}>
                <div className="text-2xl sm:text-3xl tracking-[-0.02em] font-semibold text-slate-900">{s.v}</div>
                <div className="mt-1 text-[10.5px] sm:text-[11px] uppercase tracking-[0.18em] text-slate-500 font-mono">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURE grid ─────────────────────────────────────────────────── */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 max-w-2xl">
            <div className="text-[10.5px] uppercase tracking-[0.22em] font-mono text-slate-500">Platform · Modules</div>
            <h2 className="mt-2 text-3xl sm:text-4xl tracking-[-0.02em] font-medium text-slate-900">
              Every piece of the acquisition stack, in one platform.
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {FEATURES.map((b, i) => (
              <div
                key={i}
                className="rounded-xl border border-slate-200 bg-white p-6 tilt-hover hover:shadow-soft"
                data-testid={`infra-block-${i}`}
              >
                <b.Icon className="size-5 text-slate-700" strokeWidth={1.6} />
                <h3 className="mt-5 text-base font-medium text-slate-900">{b.t}</h3>
                <p className="mt-1.5 text-sm text-slate-600">{b.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SRE metrics ──────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden py-20 bg-slate-50/60 border-y border-slate-200">
        {rain.on && <CodeRain variant="light" intensity={rain.intensity} />}
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-[10.5px] uppercase tracking-[0.22em] font-mono text-slate-500">Live · Production</div>
          <h2 className="mt-2 text-3xl sm:text-4xl tracking-[-0.02em] font-medium text-slate-900">Built for SREs and operators.</h2>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { l: "Uptime SLA",              v: "99.99%" },
              { l: "Median latency",          v: "98.6ms" },
              { l: "Daily lead volume",       v: "7.9k" },
              { l: "Active campaigns",        v: "48" },
              { l: "Compliance checks / day", v: "31k" },
              { l: "Webhook fan-outs / day",  v: "24k" },
            ].map((m, i) => (
              <div key={i} className="rounded-xl border border-slate-200 bg-white p-6" data-testid={`sre-metric-${i}`}>
                <div className="text-xs uppercase tracking-widest text-slate-500">{m.l}</div>
                <div className="mt-2 text-3xl tracking-tight font-semibold text-slate-900">{m.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ──────────────────────────────────────────────────────────── */}
      <section className="py-24 text-center">
        <div className="mx-auto max-w-3xl px-6">
          <h3 className="text-3xl sm:text-4xl tracking-[-0.02em] font-medium text-slate-900">Operate finance like infrastructure.</h3>
          <p className="mt-4 text-slate-600 text-base max-w-xl mx-auto">
            Half an hour with engineering. We'll map your stack and show you the operator view.
          </p>
          <div className="mt-6">
            <Link to="/book-demo">
              <Button className="rounded-full px-5 h-11" data-testid="infra-cta">
                Talk to engineering <ArrowRight className="size-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
